import React, { memo, useMemo } from 'react';
import { Button, Dropdown, Menu } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { useOrganization } from '@components/OrganizationBoundary';
import { useTypedSelector } from '@hooks';
import useTypedDispatch from '@hooks/useTypedDispatch';
import {
  toggleWorkspaceOrganizationDeletionModal,
  toggleWorkspaceOrganizationLeaveModal
} from '@redux/actions/modalAction';
import { organizationItemStateSelector } from '@redux/selectors/organizations';
import OrganizationAvatar from '@components/OrganizationAvatar';
import { ActionIcon } from '@icons';

type OrganizationsDropdownMenuItemProps = {
  organizationId: string;
  setIsMenuVisible: (v: boolean) => void;
  kind?: 'uxp-plugin' | 'default';
};

export default memo(function OrganizationsDropdownMenuItem({
  organizationId,
  setIsMenuVisible,
  kind = 'default'
}: OrganizationsDropdownMenuItemProps) {
  const dispatch = useTypedDispatch();
  const { currentOrganization } = useOrganization();
  const organization = useTypedSelector((state) =>
    organizationItemStateSelector(state, { organizationId })
  );
  const menuItems: ItemType[] = useMemo(() => {
    if (kind === 'uxp-plugin') return [];
    const items = [];
    if (!organization.entity?.owner.me)
      items.push({
        key: 'leave_organization',
        label: 'Leave organization',
        onClick: () => {
          if (!organization.entity) return;
          dispatch(
            toggleWorkspaceOrganizationLeaveModal({
              entity: organization.entity,
              visible: true,
              entityName: 'organization'
            })
          );
        }
      });
    if (organization.entity?.permissions.delete)
      items.push({
        key: 'delete_organization',
        label: 'Delete organization',
        onClick: () => {
          if (!organization.entity) return;
          dispatch(
            toggleWorkspaceOrganizationDeletionModal({
              entityName: 'organization',
              visible: true,
              entity: organization.entity
            })
          );
        }
      });
    return items;
  }, [dispatch, organization.entity, kind]);

  if (!organization.entity) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <div className="workspace_item">
        <OrganizationAvatar organization={organization.entity} />
      </div>
      <p className="title">{organization.entity.name}</p>
      {organization.entity.id === currentOrganization.entity?.id && (
        <span className="active_organization" />
      )}
      {menuItems.length > 0 && (
        <Dropdown
          className="select_dots"
          overlay={
            <Menu
              style={{ padding: '19px 12px' }}
              items={menuItems}
              onClick={(e) => {
                e.domEvent.stopPropagation();
                setIsMenuVisible(false);
              }}
            />
          }
          placement="bottom"
          trigger={['click']}
        >
          <Button
            onClick={(e) => e.stopPropagation()}
            type="text"
            size="small"
            style={{ padding: 0, width: 24, height: 24 }}
          >
            <ActionIcon style={{ marginRight: 0 }} />
          </Button>
        </Dropdown>
      )}
    </div>
  );
});
